<script setup lang="ts">
/**
 * Inline interactive building block for the `render_select_input` tool: a
 * single-choice dropdown collected in-chat. Emits the selected option id; the
 * dispatcher wraps it into the tool's result shape (`selectedOptionId`).
 */
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";

interface BlockOption {
  id: string;
  name: string;
  value?: number;
}

const props = withDefaults(
  defineProps<{
    input: Record<string, unknown>;
    state: string;
    disabled?: boolean;
  }>(),
  { disabled: false },
);

const emit = defineEmits<{ (e: "submit", value: string): void }>();

const { t } = useI18n({ useScope: "local" });

const title = computed(() => (typeof props.input.title === "string" ? props.input.title : ""));
const placeholder = computed(() =>
  typeof props.input.placeholder === "string" ? props.input.placeholder : t("placeholder"),
);
const options = computed<BlockOption[]>(() =>
  Array.isArray(props.input.options) ? (props.input.options as BlockOption[]) : [],
);

const resolved = computed(
  () =>
    props.disabled ||
    props.state === "output-available" ||
    props.state === "approval-responded" ||
    props.state === "output-error" ||
    props.state === "output-denied",
);

const selected = ref<string>("");
const submitted = ref(false);
const readOnly = computed(() => resolved.value || submitted.value);

function submit() {
  if (readOnly.value || !selected.value) {
    return;
  }
  submitted.value = true;
  emit("submit", selected.value);
}
</script>

<template>
  <div class="not-prose space-y-3 rounded-xl border border-border bg-card p-4" data-slot="chat-block-select">
    <p v-if="title" class="text-sm font-medium text-foreground">{{ title }}</p>

    <Select v-model="selected" :disabled="readOnly">
      <SelectTrigger class="w-full">
        <SelectValue :placeholder="placeholder" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem v-for="option in options" :key="option.id" :value="option.id">
          {{ option.name }}
        </SelectItem>
      </SelectContent>
    </Select>

    <div v-if="!resolved" class="flex justify-end">
      <Button type="button" size="sm" :disabled="!selected || submitted" @click="submit">
        {{ t("submit") }}
      </Button>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  submit: Submit
  placeholder: Select an option…
</i18n>
